import { Button } from "@mui/material"
import { useEffect, useState } from "react"
import RadioCheckedIcon from "../../assets/icons/radio-checked-icon"
import RadioIcon from "../../assets/icons/radio-icon"
import { EVENTS, JOB_PREFERENCE_EVENTS } from "../../shared/constants/MIXAPANEL_EVENTS"
import { useAssessmentStore } from "../../shared/store"
import { CompletionObjectType } from "../../shared/utils/assessment-types"
import { trackMixpanelEvent } from "../../utils/helpers"
import Shell from "../shell"

type JobPreferenceAssessmentProps = {
    handleOnCompletion: (completionObject: CompletionObjectType) => void
    handleSkip: () => void
}

const JobPreferenceAssessment = ({
    handleOnCompletion,
    handleSkip,
}: JobPreferenceAssessmentProps) => {
    const { assesmentQuestions, currentStep } = useAssessmentStore((state) => state)
    const isVasFlow = assesmentQuestions?.mandatory || false
    const mainQuestionID = assesmentQuestions?.questions[currentStep]?.questions[0]?.id
    const jobPreferenceQuestions =
        assesmentQuestions?.questions[currentStep]?.questions[0]?.sub_questions
    const questionId = assesmentQuestions?.questions[currentStep]?.questions[0]?.id
    const questionType = assesmentQuestions?.questions[currentStep]?.question_type
    const preSelectedValues = jobPreferenceQuestions.reduce(
        (preSelectedValues, question) => {
            const selected = question.data.options
                .filter((item) => item.selected)
                .map((item) => item.value)
            preSelectedValues[question.id] = selected
            return preSelectedValues
        },
        {} as Record<number, string[]>
    )
    const [selectedAnswers, setSelectedAnswers] = useState<{
        mainQuestionID: number
        answers: Record<number, string[]>
    }>({
        mainQuestionID,
        answers: preSelectedValues,
    })
    const [error, setError] = useState(false)
    const [showError, setShowError] = useState(false)

    // For showing error
    useEffect(() => {
        let errorCount = 0
        Object.values(selectedAnswers.answers).forEach((answer) => {
            if (answer.length === 0) errorCount += 1
        })
        if (errorCount > 0) {
            setError(true)
        } else {
            setError(false)
            setShowError(false)
        }
    }, [selectedAnswers])

    useEffect(() => {
        trackMixpanelEvent(EVENTS.ASSESSMENT_PAGE_SHOWN, {
            "Section Name": "Preference Reconfirm",
        })
        trackMixpanelEvent(EVENTS.ASSESSMENT_QUESTION_SHOWN, {
            question_type: "Preference Reconfirm",
        })
        trackMixpanelEvent(EVENTS.ENRICHMENT_SCREEN_SHOWN, {
            Type: isVasFlow ? "VAS" : "Application flow enrichment",
            "Entity Shown": "Preference Reconfirm",
        })
    }, [])
    const handleSkipClick = () => {
        trackMixpanelEvent(EVENTS.ASSESSMENT_SKIP_CTA_CLICK, {
            "Section Name": "Preference Reconfirm",
        })
        handleSkip()
    }

    const handleChipClick = (
        subQuestionID: number,
        option: string,
        questionTitle: string
    ) => {
        const selectedAnswersCopy = JSON.parse(JSON.stringify(selectedAnswers))
        if (
            Array.isArray(selectedAnswersCopy.answers[subQuestionID]) &&
            selectedAnswersCopy.answers[subQuestionID].length > 0
        ) {
            if (selectedAnswersCopy.answers[subQuestionID].includes(option)) {
                const index = selectedAnswersCopy.answers[subQuestionID].indexOf(option)
                selectedAnswersCopy.answers[subQuestionID].splice(index, 1)
            } else {
                selectedAnswersCopy.answers[subQuestionID].push(option)
                trackMixpanelEvent(JOB_PREFERENCE_EVENTS.JOB_PREFERENCE_SELECTED, {
                    "Section Name": "Preference Reconfirm",
                    flow: "new",
                    selection_method: "Manually Selected",
                    question_type: questionTitle,
                    selected_option: option,
                    wasPreSelected: preSelectedValues[subQuestionID].includes(option),
                })
            }
        } else {
            selectedAnswersCopy.answers[subQuestionID] = new Array(option)
        }
        setSelectedAnswers(selectedAnswersCopy)
    }
    const isChipSelected = (id: number, value: string) => {
        if (
            Array.isArray(selectedAnswers.answers[id]) &&
            selectedAnswers.answers[id].includes(value)
        )
            return true
        return false
    }
    const arrayOfQuestions = jobPreferenceQuestions.map(
        (question) => question?.data?.title
    )
    const handleNextClick = () => {
        if (error) {
            trackMixpanelEvent(JOB_PREFERENCE_EVENTS.JOB_PREFERENCE_NO_SELECTION_ERROR, {
                "Section Name": "Preference Reconfirm",
                error_text:
                    "Please select at least one preference from each of these sections",
            })
            setShowError(true)
        }
        if (!error) {
            const subAnswersArray = [] as any
            Object.entries(selectedAnswers?.answers).forEach((answer) => {
                const answerObject = {
                    sub_question_id: answer[0],
                    answers: answer[1].map((item) => item),
                }
                subAnswersArray.push(answerObject)
            })
            const completionObject = {
                question_id: questionId,
                question_type: questionType,
                sub_answers: subAnswersArray,
            }
            trackMixpanelEvent(EVENTS.ASSESSMENT_PARTIAL_EVALUATION, {
                "Section Name": "Preference Reconfirm",
                "Section Result": true,
            })
            trackMixpanelEvent(EVENTS.ASSESSMENT_ANSWERED, {
                question_type: "Preference Reconfirm",
                question_value: arrayOfQuestions,
                selected_value: selectedAnswers,
                verdict: true,
            })
            handleOnCompletion(completionObject)
        }
    }

    return (
        <Shell
            footerElement={
                <div className="flex flex-col gap-4 items-center justify-center w-full">
                    {isVasFlow && (
                        <span className="text-onSurface-secondary text-xs text-center px-[35px] ">
                            Your Application would not be Successful if you do not
                            complete this step
                        </span>
                    )}

                    <div className="flex w-full flex-col items-center justify-center px-6 mb-10 bg-white">
                        {error && showError && (
                            <p className="text-xs text-center px-12 text-error mb-4 pt-4">
                                Please select at least one preference from each of these
                                sections
                            </p>
                        )}
                        <Button
                            fullWidth
                            onClick={handleNextClick}
                            style={{
                                padding: "16px",
                                borderRadius: "4px",
                                background: `${error ? "#D1CED4" : "#2BB792"}`,
                            }}
                        >
                            <span>Next</span>
                        </Button>
                        {!isVasFlow && (
                            <Button onClick={handleSkipClick} variant="text">
                                Skip
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            <div className="pb-4">
                {jobPreferenceQuestions?.map((question) => {
                    return (
                        <div key={question?.id} className="mb-6">
                            <p className="mb-3 text-sm font-semibold text-onSurface-primary">
                                {question?.data?.title}
                            </p>
                            <div className="flex items-center flex-wrap gap-2">
                                {question?.data?.options?.map((option) => {
                                    return (
                                        <div
                                            key={option.label}
                                            className={`${
                                                isChipSelected(question.id, option.value)
                                                    ? "flex items-center p-2 rounded-3xl gap-1 bg-[#F4F2F6] border border-onSurface-disabled text-sm font-semibold text-onSurface-primary"
                                                    : "flex items-center p-2 rounded-3xl border border-[#E8E7EA] bg-white text-sm font-normal gap-1"
                                            } transition-all`}
                                            onClick={() =>
                                                handleChipClick(
                                                    question.id,
                                                    option.value,
                                                    question.data.title
                                                )
                                            }
                                        >
                                            <img
                                                src={option?.icon_url}
                                                className="h-5 w-5"
                                            />
                                            <p>{option.label}</p>
                                            {isChipSelected(question.id, option.value) ? (
                                                <RadioCheckedIcon />
                                            ) : (
                                                <RadioIcon />
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
        </Shell>
    )
}
export default JobPreferenceAssessment
